import React, { Suspense, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import { COMPLETION_PAGE_LABEL } from '@/utilities/constants';
import { TodoAPI } from '@/services/api/todo/todo';
import { isAxiosError } from 'axios';
import { useHTTPError } from 'br2s-ui-common-utils-02';
import { SpinningLoader, Toast } from 'br2s-ui-componentlibrary_r2_02';
import { useStore } from '@/redux/store';

export const IpsPaymentModule = React.lazy(
  () => import('ips/IpsPaymentModule'),
);

export interface EnforcementArnDetails {
  transactionId: string;
  transactionName: string;
  arn: string;
  dateTime: string;
}

interface PaymentStatusDetails {
  paymentStatus: string;
  arn: string;
  epRefNo: string;
  paymentMethod: string;
  amountPaid: number;
  paymentDate: string;
  customerEmail: string;
  receiptNumber?: string;
}

type ToastMessage = {
  type: 'error' | 'success' | 'informative' | undefined;
  description: string;
};

const initialState = {
  transactionId: '',
  transactionName: '',
  arn: '',
  dateTime: '',
};

const Payment = () => {
  const [toastMessage, setToastMessage] = useState<ToastMessage | null>(null);
  const [enforcementPaymentDetails, setEnforcementPaymentDetails] =
    useState<EnforcementArnDetails>(initialState);
  const [paymentStatusDetails, setPaymentStatusDetails] =
    useState<PaymentStatusDetails>({
      paymentStatus: '',
      arn: '',
      epRefNo: '',
      paymentMethod: '',
      amountPaid: 0.0,
      paymentDate: '',
      customerEmail: '',
      receiptNumber: '',
    });
  const { enforcementId } = useParams();
  const { toggleLoading } = useStore();
  const navigate = useNavigate();
  const { handleHTTPError } = useHTTPError();

  useEffect(() => {
    fetchARN();
  }, []);

  // istanbul ignore next
  const fetchARN = async () => {
    try {
      toggleLoading(true);
      const response =
        await TodoAPI.getEnforcementPaymentDetails(enforcementId);
      setEnforcementPaymentDetails(response.data.result);
    } catch (error) {
      console.error(error);

      if (isAxiosError(error) && error.response?.status === 400) {
        setToastMessage({
          type: 'error',
          description: error?.response?.data?.error.errorDesc,
        });
      }

      handleHTTPError(error);
    } finally {
      toggleLoading(false);
    }
  };

  // istanbul ignore next
  const closeToast = () => setToastMessage(null);

  return (
    <>
      <Suspense fallback={<SpinningLoader isFullScreen />}>
        {enforcementPaymentDetails?.arn && (
          <IpsPaymentModule
            paymentArn={enforcementPaymentDetails?.arn}
            paymentStatusDetails={paymentStatusDetails}
            handleSetPaymentStatusDetails={setPaymentStatusDetails}
            handleNavigateBack={() => navigate('/dashboard')}
            handleSetToastMessage={() => undefined}
            paymentConfirmSecondaryButtonConfig={{
              label: COMPLETION_PAGE_LABEL.GO_TO_DASHBOARD,
              icon: SystemIcon.DocumentDocument,
            }}
          />
        )}
      </Suspense>

      <Toast
        type={toastMessage?.type}
        isDisplayed={!!toastMessage}
        headline=""
        description={toastMessage?.description}
        onDismissClick={closeToast}
        onDismiss={closeToast}
      />
    </>
  );
};

export default Payment;
